import Vue from 'vue';
import type { DirectiveOptions, DirectiveBinding } from 'vue/types/options';

// Define custom properties on the HTMLElement type for better TypeScript support.
declare global {
    interface HTMLElement {
        _classObserver?: MutationObserver;
        _updatePositionCallback?: () => void;
        _setBaseStylesCallback?: () => void;
        _resetStylesCallback?: () => void;
        _parentScrollListener?: () => void;
    }
}

const wrapperClass: string = '.multiselect__content-wrapper';
const tagsClass: string = '.multiselect__tags';

/**
 * Sets the essential, one-time styles when the dropdown opens.
 */
const setBaseStyles = (el: HTMLElement): void => {
    const wrapperElement = el.querySelector<HTMLElement>(wrapperClass);
    if (!wrapperElement) return;

    wrapperElement.style.display = 'block';
    wrapperElement.style.minWidth = '0';
    wrapperElement.style.position = 'fixed';
    wrapperElement.style.bottom = 'auto';
};

/**
 * Calculates and applies the dynamic position and width of the dropdown.
 */
const updatePosition = (el: HTMLElement): void => {
    const wrapperElement = el.querySelector<HTMLElement>(wrapperClass);
    const tagsElement = el.querySelector<HTMLElement>(tagsClass);

    if (!wrapperElement || !tagsElement || !el.classList.contains('multiselect--active')) {
        return;
    }

    requestAnimationFrame(() => {
        const { top, left, height, width } = tagsElement.getBoundingClientRect();
        const wrapperHeight: number = wrapperElement.getBoundingClientRect().height;
        const isAbove: boolean = el.classList.contains('multiselect--above');

        wrapperElement.style.width = `${width}px`;
        wrapperElement.style.left = `${left}px`;
        const newTop: number = isAbove ? top - wrapperHeight - 16 : top + height;
        wrapperElement.style.top = `${newTop}px`;
    });
};

/**
 * Resets all inline styles when the dropdown closes.
 */
const resetStyles = (el: HTMLElement): void => {
    const wrapperElement = el.querySelector<HTMLElement>(wrapperClass);
    if (wrapperElement) {
        wrapperElement.style.position = '';
        wrapperElement.style.top = '';
        wrapperElement.style.left = '';
        wrapperElement.style.width = '';
        wrapperElement.style.bottom = '';
        wrapperElement.style.minWidth = '';
        wrapperElement.style.display = 'none';
    }
};

const directive: DirectiveOptions = {
    bind(el: HTMLElement) {
        const contentWrapper = el.querySelector('.multiselect__content-wrapper') as HTMLElement;
        if (contentWrapper) {
            contentWrapper.style.width = 'auto';
            contentWrapper.style.display = 'none';
        }
    },

    inserted(el: HTMLElement, binding: DirectiveBinding): void {
        /**
         * @constant {HTMLElement} targetElement
         * @description The element that receives the 'multiselect--active' class.
         * This handles the specific wrapper component scenario described.
         * If the element is not found, it falls back to using `el` itself for safety.
         */
        const targetElement = el.querySelector<HTMLElement>('.multiselect') || el;

        // All subsequent logic will now correctly use `targetElement` instead of `el`.
        el._setBaseStylesCallback = () => setBaseStyles(targetElement);
        el._updatePositionCallback = () => updatePosition(targetElement);
        el._resetStylesCallback = () => resetStyles(targetElement);

        // This handler now correctly checks the class list of the child element.
        const handleClassChange = () => {
            if (targetElement.classList.contains('multiselect--active')) {
                el._setBaseStylesCallback!();
                setTimeout(el._updatePositionCallback!, 0);
            } else {
                el._resetStylesCallback!();
            }
        };

        /**
         * @description Observer that monitors changes to the class attribute of the specified target element.
         * @remarks
         * IMPORTANT: We attach the observer to the `targetElement`, not the wrapper `el`.
         */
        el._classObserver = new MutationObserver(handleClassChange);
        el._classObserver.observe(targetElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        // Setup scroll listeners which correctly call the callbacks that operate on the targetElement.
        const rawValue = binding.value;
        const selector: string | undefined = typeof rawValue === 'string' ? rawValue : undefined;
        const parentElement: HTMLElement | null = selector ? document.querySelector<HTMLElement>(selector) : null;
        if (parentElement) {
            el._parentScrollListener = el._updatePositionCallback;
            parentElement.addEventListener('scroll', el._parentScrollListener);
        }
        document.addEventListener('scroll', el._updatePositionCallback, true);
    },

    unbind(el: HTMLElement, binding: DirectiveBinding): void {
        el._classObserver?.disconnect();

        const rawValue = binding.value;
        const selector: string | undefined = typeof rawValue === 'string' ? rawValue : undefined;
        const parentElement: HTMLElement | null = selector ? document.querySelector<HTMLElement>(selector) : null;

        if (parentElement && el._parentScrollListener) {
            parentElement.removeEventListener('scroll', el._parentScrollListener);
        }
        if (el._updatePositionCallback) {
            document.removeEventListener('scroll', el._updatePositionCallback, true);
        }

        el._resetStylesCallback?.();

        delete el._classObserver;
        delete el._setBaseStylesCallback;
        delete el._updatePositionCallback;
        delete el._resetStylesCallback;
        delete el._parentScrollListener;
    },
};

/**
 * @summary A Vue directive to make a dropdown panel "float" relative to the viewport.
 * @description Solves the common UI problem where a dropdown menu inside a container with `overflow: hidden` or `overflow: scroll` gets clipped.
 * This directive detaches the dropdown panel from the normal document flow by giving it `position: fixed`. It then manually calculates
 * and continuously updates the panel's `top` and `left` properties to ensure it always appears anchored to its parent input,
 * regardless of any parent container's scroll position or clipping. It is specifically designed to work with components
 * that follow the `vue-multiselect` component's class structure.
 *
 * @example
 * <!-- Basic Usage -->
 * <!-- Attach the directive to your VSelect/multiselect component. -->
 * <VSelect v-floating-select ... />
 *
 * @example
 * <!-- Advanced Usage with a Scrolling Parent Container -->
 * <!-- If the select is inside a specific scrollable container (not the whole page),
 *      pass the selector for that container as the directive's value.
 *      This allows the directive to also listen for scroll events on that specific container. -->
 * <div class="my-custom-scroll-container">
 *   <VSelect v-floating-select="'.my-custom-scroll-container'" ... />
 * </div>
 *
 * @param {HTMLElement} el - The multiselect component's root element that the directive is bound to.
 */
Vue.directive('floating-select', directive);
